import Vue from "vue";
import axios from "axios";
import { getSidebarItems } from "@/config/navigationItems";

/**
 * The tenant configuration values the app shell reads, fetched once per session and held on the
 * root instance beside `userSettings`/`userSettingsLoaded`, which it deliberately mirrors.
 *
 * Only the fields named by `tenantConfigFields()` are fetched, never the whole row: this runs on
 * every page load for every signed-in user, and the full row carries integration secrets. A field
 * missing from that list reads as undefined here, so a new consumer adds its field to the list.
 *
 * `tenantConfigLoaded` exists because an empty `tenantConfig` cannot tell "the fetch has not
 * landed yet" from "the tenant has nothing set". Every consumer that would switch a feature ON must
 * wait for the flag, so a failed fetch leaves features hidden rather than showing something a
 * tenant has disabled. `tenantFlag()` returns false until then for exactly that reason.
 *
 * There is no configuration-changed broadcast, so a toggle flipped on the Configuration page
 * reaches other users on their next page load -- or up to a minute after that, since CPHub.API
 * serves these values from a 60-second per-process cache that a save evicts only on the instance
 * that took it. That matches every other setting today.
 */

/** The onboarding toggles the welcome gate, the footer and the guided tour read. */
const ONBOARDING_FIELDS = ["sandboxMode", "disableWelcomeModal", "disableGuidedTour"];

/**
 * Every configuration field this store serves: the flags config-driven nav items name, plus the
 * onboarding toggles. The response is keyed by the names as sent, so they are looked up as written.
 */
export function tenantConfigFields(): string[] {
    // The queue names only label print-queue items; they have no bearing on which flags are named.
    const navFlags = getSidebarItems("", "")
        .flatMap(item => [item, ...(item.children ?? [])])
        .map(item => item.requiresConfigFlag)
        .filter((flag): flag is string => !!flag);
    return [...new Set([...navFlags, ...ONBOARDING_FIELDS])];
}

let appInstance: Vue | null = null;
/** Set while a fetch is outstanding, and kept after it succeeds so the session fetches once. */
let loadPromise: Promise<void> | null = null;

function root(): Record<string, any> | null {
    return (appInstance?.$root as any)?.$data ?? null;
}

/**
 * Binds the root instance. Called from main.ts before `$mount`, so the accessors below are already
 * reading reactive root data by the time any component first renders -- a component that rendered
 * before the binding would cache a value with no reactive dependency and never update.
 */
export function initTenantConfig(app: Vue): void {
    appInstance = app;
}

/**
 * Fetches the configuration values once per session. Repeated calls share the in-flight promise and
 * resolve immediately once it has succeeded; a failed fetch is retried by the next call, since the
 * alternative is a session with every gated feature permanently hidden. DashboardLayout calls this
 * at mount and again on every navigation, which is what makes that retry happen.
 */
export function loadTenantConfig(): Promise<void> {
    if (loadPromise) return loadPromise;

    loadPromise = axios.post<Record<string, any>>("/Configuration/values", tenantConfigFields())
        .then(response => {
            const data = root();
            if (!data) return;
            // `tenantConfig` and `tenantConfigLoaded` are declared on the root's data, so plain
            // assignment is reactive; the order matters, because a watcher on the flag reads the
            // values as soon as it fires.
            data.tenantConfig = response.data ?? {};
            data.tenantConfigLoaded = true;
        })
        .catch(() => {
            // Only a load that has never succeeded can fail, since a success keeps its promise,
            // so the loaded flag is still false and there is nothing to put back.
            loadPromise = null;
        });

    return loadPromise;
}

/** The fetched configuration values, or an empty object before the fetch lands. */
export function tenantConfig(): Record<string, any> {
    return root()?.tenantConfig ?? {};
}

export function isTenantConfigLoaded(): boolean {
    return root()?.tenantConfigLoaded === true;
}

/**
 * A boolean flag off the configuration values. False until the fetch lands, so no state of this
 * service can enable a feature the tenant has switched off.
 */
export function tenantFlag(name: string): boolean {
    if (!isTenantConfigLoaded()) return false;
    return tenantConfig()[name] === true;
}

/** Test seam: drops the cached app and the once-per-session promise. */
export function _resetForTests(): void {
    appInstance = null;
    loadPromise = null;
}
